import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, RefreshCw, ShieldAlert } from 'lucide-react';
import { Badge, Button, RobbieMark } from '../../ui';
import { AwardPlan, QuoteRecommendation } from '../../../types/quote.types';

interface StrategyBarProps {
    recommendation: QuoteRecommendation;
    awardPlan: AwardPlan | null;
    isAccepted: boolean;
    readOnly: boolean;
    busy: boolean;
    onRegenerate: () => void;
}

const StrategyBar: React.FC<StrategyBarProps> = ({ recommendation, awardPlan, isAccepted, readOnly, busy, onRegenerate }) => {
    const [showStrategy, setShowStrategy] = useState(false);
    const confidencePercent = recommendation.confidence != null ? Math.round(recommendation.confidence * 100) : null;
    return (
        <div className="rounded-card border border-robbie-border bg-ai-chip px-s5 py-s4">
            <div className="flex flex-wrap items-center justify-between gap-s3">
                <div className="flex min-w-0 items-center gap-s3">
                    <RobbieMark size="xs" state="insight" aria-label="Robbie's plan" />
                    <span className="text-row-title text-ink">Robbie's suggested plan is pre-filled below</span>
                    {isAccepted && (
                        <span className="inline-flex items-center gap-[3px] rounded-[4px] bg-success-bg px-[7px] py-[1px] text-[10.5px] font-semibold text-success-ink">
                            <CheckCircle2 className="h-[11px] w-[11px]" />
                            {awardPlan?.source === 'USER_ADJUSTED' ? 'confirmed — adjusted by you' : 'confirmed — Robbie\'s plan'}
                        </span>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-s3">
                    {confidencePercent != null && (
                        <Badge tone="robbie" mark={false} mono>
                            {confidencePercent}% confident
                        </Badge>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowStrategy(v => !v)}
                        className="flex items-center gap-s1 text-meta font-semibold text-robbie hover:text-robbie-ink"
                    >
                        Strategy &amp; risks
                        <ChevronDown className={`h-[13px] w-[13px] transition-transform ${showStrategy ? 'rotate-180' : ''}`} />
                    </button>
                    {!readOnly && (
                        <Button variant="secondary" size="sm" iconLeft={<RefreshCw />} disabled={busy} onClick={onRegenerate}>
                            Regenerate
                        </Button>
                    )}
                </div>
            </div>

            {showStrategy && (
                <div className="mt-s3 flex flex-col gap-s2 border-t border-robbie-border-soft pt-s3 text-body-sm text-ink-body">
                    <p className="m-0">{recommendation.overallSummary}</p>
                    {recommendation.portfolioRisks.map(risk => (
                        <div key={risk} className="flex items-start gap-s2 text-warn-ink">
                            <ShieldAlert className="mt-[2px] h-[13px] w-[13px] shrink-0" />
                            <span>{risk}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StrategyBar;
